/**
 * AdminGiftsAdoptionPage — iter1118c
 *
 * Админ табло, което показва всички пациенти с активен „Подарък" —
 * тяхното усвояване, активност, статус. Помага за проследяване на
 * кохорта (напр. „Февруари 2026 — първа кохорта").
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import { Loader2, Users, Activity, Clock, Check } from "lucide-react";

const FILTERS = [
  { id: "", label: "Всички" },
  { id: "active", label: "Активни (7 дни)" },
  { id: "never_used", label: "Не са използвали" },
  { id: "exhausted", label: "Изчерпани" },
  { id: "expired", label: "Изтекли" },
];

export default function AdminGiftsAdoptionPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [filter, setFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/gifts/adoption`);
        setData(r.data);
      } catch { /* тих провал */ }
      setLoading(false);
    })();
  }, [API]);

  const bg = isDark ? "#0B0F19" : "#F8FAFC";
  const card = isDark ? "#111827" : "#FFFFFF";
  const text = isDark ? "#E2E8F0" : "#0F172A";
  const muted = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "#1F2937" : "#E2E8F0";

  if (loading) {
    return (
      <div style={{ background: bg, minHeight: "100vh", color: text }}>
        <MobileTopBar />
        <div style={{ padding: 80, display: "flex", justifyContent: "center" }}>
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const items = data?.items || [];

  const filtered = items.filter((it) => {
    if (filter === "active") return it.active_last_7d;
    if (filter === "never_used") return !it.first_ai_at;
    if (filter === "exhausted") return it.credits_current <= 0;
    if (filter === "expired") return it.trial_expired;
    return true;
  });

  return (
    <div style={{ background: bg, minHeight: "100vh", color: text, paddingBottom: 60 }}
         data-testid="admin-gifts-adoption-page">
      <MobileTopBar />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>
          Подаръци — усвояване
        </h1>

        {/* Обобщени метрики */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10, marginBottom: 20,
        }}>
          <Stat card={card} border={border} muted={muted} text={text}
                icon={<Users size={14} />} label="Общо подаръци" value={stats.total || 0} />
          <Stat card={card} border={border} muted={muted} text={text}
                icon={<Activity size={14} />} label="Активни (7 дни)"
                value={stats.active_last_7_days || 0} color="#10B981" />
          <Stat card={card} border={border} muted={muted} text={text}
                icon={<Clock size={14} />} label="Не са използвали"
                value={stats.never_used || 0} color="#F59E0B" />
          <Stat card={card} border={border} muted={muted} text={text}
                icon={<Check size={14} />} label="Изчерпани"
                value={stats.exhausted || 0} color="#8B5CF6" />
          <Stat card={card} border={border} muted={muted} text={text}
                icon={<Clock size={14} />} label="Изтекли"
                value={stats.trial_expired || 0} color="#EF4444" />
        </div>

        {/* Филтри */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          {FILTERS.map((f) => (
            <button
              key={f.id || "all"}
              onClick={() => setFilter(f.id)}
              data-testid={`adoption-filter-${f.id || "all"}`}
              style={{
                padding: "6px 12px", borderRadius: 999, fontSize: 13,
                border: `1px solid ${filter === f.id ? "#0EA5E9" : border}`,
                background: filter === f.id ? "#0EA5E922" : "transparent",
                color: filter === f.id ? "#0EA5E9" : muted,
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Таблица на пациентите */}
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: muted }}>
            Няма записи в тази категория.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((it) => (
              <div key={it.user_id}
                   data-testid={`adoption-row-${it.user_id}`}
                   style={{
                     background: card, border: `1px solid ${border}`,
                     borderRadius: 10, padding: 14,
                     display: "grid",
                     gridTemplateColumns: "2fr 1.5fr 1fr 1fr",
                     gap: 12, alignItems: "center",
                   }}>
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <strong style={{ fontSize: 14 }}>{it.name || it.email}</strong>
                    {it.active_last_7d && (
                      <span style={{
                        padding: "2px 6px", borderRadius: 999, fontSize: 10,
                        background: "#10B98122", color: "#10B981",
                      }}>Активен</span>
                    )}
                    {it.trial_expired && (
                      <span style={{
                        padding: "2px 6px", borderRadius: 999, fontSize: 10,
                        background: "#EF444422", color: "#EF4444",
                      }}>Изтекъл</span>
                    )}
                    {!it.first_ai_at && (
                      <span style={{
                        padding: "2px 6px", borderRadius: 999, fontSize: 10,
                        background: "#F59E0B22", color: "#F59E0B",
                      }}>Не е ползвал</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
                    {it.email} · {it.gift_code}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: muted }}>Изразходени кредити</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {it.credits_used} / {it.credits_initial}
                    <span style={{ color: muted, fontWeight: 400, fontSize: 12 }}>
                      {" "}({it.pct_used}%)
                    </span>
                  </div>
                  <div style={{ height: 4, background: border, borderRadius: 999, marginTop: 4, overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${Math.min(100, it.pct_used)}%`,
                      background: it.pct_used > 80 ? "#EF4444" : "#0EA5E9",
                    }} />
                  </div>
                </div>

                <div style={{ fontSize: 12, color: muted }}>
                  <div>Включен:</div>
                  <div style={{ color: text }}>
                    {it.applied_at
                      ? new Date(it.applied_at).toLocaleDateString("bg-BG")
                      : "—"}
                  </div>
                </div>

                <div style={{ fontSize: 12, color: muted }}>
                  <div>Пробен период:</div>
                  <div style={{ color: text }}>
                    {it.days_left != null ? `${it.days_left} дни` : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ card, border, muted, text, icon, label, value, color }) {
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                    color: muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || text, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}
